import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { AuthProvider, useAuth } from '@hooks/useAuth';
import { useWhatsAppEnabled } from '@hooks/useWhatsAppEnabled';
import { useImplantacaoConsole } from '@hooks/useImplantacaoConsole';
import { usePodeVerClientesHub } from '@hooks/usePodeVerClientesHub';
import { useListaVidasPapel } from '@hooks/useListaVidasPapel';
import { useUsageHeartbeat } from '@hooks/useUsageHeartbeat';
import type { OperationalRole } from '@/types/auth';
import Layout from '@components/Layout/Layout';
import ErrorBoundary from '@components/ErrorBoundary';
import { isEnterpriseWorkspace } from '@config/enterprise';

// Code-splitting por rota (React.lazy) — cada página vira um chunk separado,
// cortando o JS inicial. O <Suspense> em AppRoutes cobre o load do chunk.
// Layout/ErrorBoundary ficam eager (shell sempre presente).
const Login = lazy(() => import('@pages/Login'));
const SuperAdmin = lazy(() => import('@pages/SuperAdmin'));
const KanbanPage = lazy(() => import('@pages/KanbanPage'));
const DashboardPage = lazy(() => import('@pages/DashboardPage'));
const LeadsPage = lazy(() => import('@pages/LeadsPage'));
const EmpresasPage = lazy(() => import('@pages/EmpresasPage'));
const LeadDetail = lazy(() => import('@components/LeadDetail'));
const SettingsPage = lazy(() => import('@pages/SettingsPage'));
const ActivitiesPage = lazy(() => import('@pages/ActivitiesPage'));
const CallsPage = lazy(() => import('@pages/CallsPage'));
const AgendaPage = lazy(() => import('@pages/AgendaPage'));
const GoalsPage = lazy(() => import('@pages/GoalsPage'));
const MyGoalsPage = lazy(() => import('@pages/MyGoalsPage'));
const CommissionsPage = lazy(() => import('@pages/CommissionsPage'));
const MyPanelPage = lazy(() => import('@pages/MyPanelPage'));
const ClientLivesPage = lazy(() => import('@pages/ClientLivesPage'));
const ClientesPage = lazy(() => import('@pages/ClientesPage'));
const ClienteFichaPage = lazy(() => import('@pages/ClienteFichaPage'));
const TeamUsagePage = lazy(() => import('@pages/TeamUsagePage'));
const WhatsAppPage = lazy(() => import('@pages/WhatsAppPage'));
const ListaVidasPage = lazy(() => import('@pages/ListaVidasPage'));
// /meu-perfil agora redireciona pra /settings (aba Disponibilidade).
const AcceptInvitePage = lazy(() => import('@pages/AcceptInvitePage'));
const ResetPasswordPage = lazy(() => import('@pages/ResetPasswordPage'));
const PendingPage = lazy(() => import('@pages/PendingPage'));
const ReferralPage = lazy(() => import('@pages/ReferralPage'));
const FormalizacaoLinkPage = lazy(() => import('@pages/FormalizacaoLinkPage'));
const ContratoFormPage = lazy(() => import('@pages/ContratoFormPage'));
// Telas mobile dedicadas ao Enterprise (casca + cadastro + lista).
const MobileShell = lazy(() => import('@components/pages/MobileEnterprise/MobileShell'));
const MobileNewLead = lazy(() => import('@components/pages/MobileEnterprise/NewLeadForm'));
const MobileEditLead = lazy(() => import('@components/pages/MobileEnterprise/EditLeadForm'));
const MobileMyLeads = lazy(() => import('@components/pages/MobileEnterprise/MyLeadsList'));

// Tela de carregamento (antes .loading-screen em styles/loading.css).
const LOADING_SCREEN = 'min-h-screen flex items-center justify-center bg-navy text-white text-lg font-medium';

// Componente para proteger rotas que exigem autenticação.
// Se o profile estiver pendente, redireciona para /pending.
function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, profile, loading } = useAuth();

  if (loading) {
    return <div className={LOADING_SCREEN}>Carregando...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (profile?.pendente) {
    return <Navigate to="/pending" replace />;
  }

  return <>{children}</>;
}

// Guard de role: redireciona para /kanban se o user não tem permissão.
// canViewAll cobre gestor, owner administrativo e is_owner global.
// allow: array de roles permitidos ('gestor' | 'closer' | 'sdr')
//        OR 'gestor-only' como atalho para "só quem canViewAll".
type RoleAllow = 'gestor-only' | OperationalRole[];

function RoleGuard({ allow = 'gestor-only', children }: { allow?: RoleAllow; children: ReactNode }) {
  const { loading, profile, canViewAll, userRole } = useAuth();
  if (loading) return <div className={LOADING_SCREEN}>Carregando...</div>;
  if (profile?.pendente) return <Navigate to="/pending" replace />;

  const canAll = typeof canViewAll === 'function' ? canViewAll() : false;
  if (allow === 'gestor-only') {
    if (!canAll) return <Navigate to="/kanban" replace />;
    return <>{children}</>;
  }
  const role = typeof userRole === 'function' ? userRole() : null;
  if (canAll) return <>{children}</>;
  if (Array.isArray(allow) && role && (allow as string[]).includes(role)) return <>{children}</>;
  return <Navigate to="/kanban" replace />;
}

// Atendimento (WhatsApp): liberado por workspace no Super Admin. O backend
// checa a mesma flag — isto aqui é só para não deixar a tela abrir vazia.
function WhatsAppRoute({ children }: { children: ReactNode }) {
  const { loading } = useAuth();
  const { enabled, loading: featureLoading } = useWhatsAppEnabled();
  if (loading || featureLoading) return <div className={LOADING_SCREEN}>Carregando...</div>;
  if (!enabled) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

// Área de Clientes (/clientes): aba Hub + fila de Implantação. Entra quem é
// do grupo de Implantação (como sempre) OU quem `cs.pode_ver_clientes_hub`
// libera (owner, CS, Implantação — é a mesma RPC que decide a aba Hub).
// Nem workspace ativo nem role aqui: quem responde são as RPCs, direto.
// Redirecionar é só para não abrir uma tela que vai negar nas duas.
function ImplantacaoRoute({ children }: { children: ReactNode }) {
  const { loading } = useAuth();
  const { enabled: implantacaoEnabled, loading: grupoLoading } = useImplantacaoConsole();
  const { enabled: hubEnabled, loading: hubLoading } = usePodeVerClientesHub();
  if (loading || grupoLoading || hubLoading) return <div className={LOADING_SCREEN}>Carregando...</div>;
  if (!implantacaoEnabled && !hubEnabled) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// Lista de Vidas: quem tem papel (cs | admin) em lista_vidas.papeis. Sem papel,
// nem menu nem rota. O servidor confere o papel de novo em cada ação.
// Falha ao descobrir o papel (rede, auth indisponível) NÃO é "sem papel": mostra
// erro com nova tentativa em vez de expulsar quem tem acesso. Só redireciona
// quando o servidor respondeu `papel: null`.
function ListaVidasRoute({ children }: { children: ReactNode }) {
  const { loading } = useAuth();
  const { papel, semPapel, erro, loading: papelLoading, refetch } = useListaVidasPapel();
  if (loading || papelLoading) return <div className={LOADING_SCREEN}>Carregando...</div>;
  if (erro) {
    return (
      <div className={`${LOADING_SCREEN} flex-col gap-3`} role="alert">
        <div>Não foi possível verificar seu acesso à Lista de vidas.</div>
        <button type="button" className="rounded-md border border-white/40 px-3 py-1.5 text-sm" onClick={refetch}>
          Tentar de novo
        </button>
      </div>
    );
  }
  if (semPapel) return <Navigate to="/" replace />;
  if (!papel) return <div className={LOADING_SCREEN}>Carregando...</div>;
  return <>{children}</>;
}

function EnterpriseHiddenRoute({ children }: { children: ReactNode }) {
  const { loading, workspace } = useAuth();
  if (loading) return <div className={LOADING_SCREEN}>Carregando...</div>;
  if (isEnterpriseWorkspace(workspace)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

// Espelho do EnterpriseHiddenRoute: rotas que só existem no Enterprise.
function EnterpriseOnlyRoute({ children }: { children: ReactNode }) {
  const { loading, workspace } = useAuth();
  if (loading) return <div className={LOADING_SCREEN}>Carregando...</div>;
  if (!isEnterpriseWorkspace(workspace)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

// Detecta viewport mobile de forma síncrona (sem flash no 1º paint) e
// reage a resize/rotação. Espelha o breakpoint do use-mobile (768px).
function useIsMobileViewport() {
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const onChange = () => setMobile(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return mobile;
}

// No mobile + Enterprise, o app abre nas telas dedicadas (/m). Aqui
// redirecionamos as rotas desktop pra lá; desktop e não-Enterprise seguem
// no Layout normal, sem qualquer alteração de comportamento.
function DesktopLayoutGate() {
  const isMobile = useIsMobileViewport();
  const { workspace } = useAuth();
  if (isMobile && isEnterpriseWorkspace(workspace)) {
    return <Navigate to="/m/meus-leads" replace />;
  }
  return <Layout />;
}

// Espelho inverso: as telas /m só valem pra mobile + Enterprise. Desktop
// ou outros workspaces caindo em /m voltam pro Layout desktop.
function MobileEnterpriseGate({ children }: { children: ReactNode }) {
  const isMobile = useIsMobileViewport();
  const { loading, workspace } = useAuth();
  if (loading) return <div className={LOADING_SCREEN}>Carregando...</div>;
  if (!isMobile || !isEnterpriseWorkspace(workspace)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// Componente para redirecionar para home se já estiver logado
function PublicRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();

  if (loading) {
    return <div className={LOADING_SCREEN}>Carregando...</div>;
  }

  if (user) {
    const requestedNext = searchParams.get('next');
    const next = requestedNext?.startsWith('/') && !requestedNext.startsWith('//')
      ? requestedNext
      : '/';
    return <Navigate to={next} replace />;
  }

  return <>{children}</>;
}

// Permite acesso autenticado OU não autenticado (página de aceite de
// convite, que pode ser aberta sem sessão). Sem redirect.
function HybridRoute({ children }: { children: ReactNode }) {
  const { loading } = useAuth();
  if (loading) return <div className={LOADING_SCREEN}>Carregando...</div>;
  return <>{children}</>;
}

function AppRoutes() {
  // Presença amostrada. Fica aqui, e não no Layout, porque o Enterprise
  // mobile roda sob /m (MobileShell) e ficaria fora da contagem.
  const { user, workspace } = useAuth();
  useUsageHeartbeat(user?.id, workspace?.id, workspace);

  return (
    <BrowserRouter>
      <Suspense fallback={<div className={LOADING_SCREEN}>Carregando…</div>}>
      <Routes>
        <Route path="/login" element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } />

        {/* Aceite de convite — público, mas funciona logado também */}
        <Route path="/accept-invite/:token" element={
          <HybridRoute>
            <AcceptInvitePage />
          </HybridRoute>
        } />

        {/* Redefinir senha — público. Hybrid (e não PublicRoute) porque o
            link pode ser aberto num navegador com sessão viva, e mandar a
            pessoa para o dashboard seria justamente não deixá-la trocar
            a senha. */}
        <Route path="/reset-password/:token" element={
          <HybridRoute>
            <ResetPasswordPage />
          </HybridRoute>
        } />

        {/* Tela de aguardo de aprovação — só faz sentido logado */}
        <Route path="/pending" element={
          <HybridRoute>
            <PendingPage />
          </HybridRoute>
        } />

        {/* Formulário de indicação por token — público, inclusive logado. */}
        <Route path="/indicacao/:token" element={<HybridRoute><ReferralPage /></HybridRoute>} />
        <Route path="/formalizacao/:token" element={<HybridRoute><FormalizacaoLinkPage /></HybridRoute>} />

        {/* Dados do contrato preenchidos pelo próprio cliente — público. */}
        <Route path="/contrato/:token" element={<HybridRoute><ContratoFormPage /></HybridRoute>} />

        {/* Telas mobile dedicadas ao Enterprise (fora do Layout desktop) */}
        <Route path="/m" element={
          <ProtectedRoute>
            <MobileEnterpriseGate>
              <MobileShell />
            </MobileEnterpriseGate>
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="/m/meus-leads" replace />} />
          <Route path="meus-leads" element={<MobileMyLeads />} />
          <Route path="novo-lead" element={<MobileNewLead />} />
          <Route path="lead/:leadId" element={<MobileEditLead />} />
        </Route>

        <Route path="/" element={
          <ProtectedRoute>
            <DesktopLayoutGate />
          </ProtectedRoute>
        }>
          <Route index element={<RoleGuard allow={['gestor', 'closer', 'sdr']}><DashboardPage /></RoleGuard>} />
          <Route path="kanban" element={<KanbanPage />} />
          <Route path="activities" element={<ActivitiesPage />} />
          <Route path="calls" element={<CallsPage />} />
          <Route path="agenda" element={<AgendaPage />} />
          <Route path="leads" element={<LeadsPage />} />
          <Route path="atendimento" element={<WhatsAppRoute><WhatsAppPage /></WhatsAppRoute>} />
          <Route path="lista-vidas" element={<ListaVidasRoute><ListaVidasPage /></ListaVidasRoute>} />
          <Route path="empresas" element={<EmpresasPage />} />
          <Route path="leads/:leadId" element={<LeadDetail />} />
          <Route path="goals" element={<EnterpriseHiddenRoute><GoalsPage /></EnterpriseHiddenRoute>} />
          <Route path="my-goals" element={<EnterpriseHiddenRoute><MyGoalsPage /></EnterpriseHiddenRoute>} />
          <Route path="commissions" element={<EnterpriseHiddenRoute><CommissionsPage /></EnterpriseHiddenRoute>} />
          <Route path="dashboard" element={<RoleGuard allow={['gestor', 'closer', 'sdr']}><DashboardPage /></RoleGuard>} />
          <Route path="my-panel" element={<MyPanelPage />} />
          {/* /clientes tem duas abas: o espelho do Hub (owner, CS e
              Implantação) e a fila de Implantação (só o grupo) — o guard
              deixa entrar quem vê qualquer uma das duas.
              /clientes/:empresaId virou a FICHA única: quem decide o
              que aparece é a RPC `cs.obter_ficha_cliente` (completo para o
              CS, reduzido para Implantação) — por isso NÃO leva
              ImplantacaoRoute, que bloquearia o CS de abrir a própria ficha.
              Mais específica primeiro não é necessário — o ranking do
              react-router já dá a rota de /vidas como a mais específica das
              três. */}
          <Route path="clientes" element={<ImplantacaoRoute><ClientesPage /></ImplantacaoRoute>} />
          <Route path="clientes/:empresaId" element={<ClienteFichaPage />} />
          {/* Vidas do cliente. Sem guard de Enterprise: quem opera é o CS, e
              o workspace dele não é enterprise — quem barra é a RLS do schema
              `cs`, que já sabe quem pode ler o quê. */}
          <Route path="clientes/:empresaId/vidas" element={<ClientLivesPage />} />
          {/* Sem RoleGuard: colaborador vê o próprio uso, gestor vê o da equipe. */}
          <Route path="equipe" element={<EnterpriseOnlyRoute><TeamUsagePage /></EnterpriseOnlyRoute>} />
          {/* /meu-perfil agora redireciona pra /settings (aba Disponibilidade) */}
          <Route path="my-profile" element={<Navigate to="/settings" replace />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="super-admin" element={
            <RoleGuard allow="gestor-only"><SuperAdmin /></RoleGuard>
          } />
        </Route>
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppRoutes />
        {/* Toaster global. visibleToasts limita pilha; richColors usa
            paleta verde/vermelho consistente do sonner. */}
        <Toaster
          position="top-right"
          richColors
          closeButton
          visibleToasts={3}
          duration={4000}
        />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
